/**
 * GalleryImageReplyDisplay Component
 * Shows gallery image preview above message bubble when message replies to a gallery image
 * Clean design with proper aspect ratio preservation
 */

interface GalleryImageReplyDisplayProps {
  galleryImage: {
    id: string
    title: string | null
    thumbnail_url: string
    original_width?: number
    original_height?: number
  }
  isSentByMe: boolean
}

export function GalleryImageReplyDisplay({ galleryImage, isSentByMe }: GalleryImageReplyDisplayProps) {
  // Calculate aspect ratio for proper image display
  const aspectRatio = (galleryImage.original_width && galleryImage.original_height)
    ? galleryImage.original_width / galleryImage.original_height
    : 1

  // Max dimensions for the preview (smaller than composer preview)
  const maxHeight = 56 // px
  const maxWidth = 80 // px

  // Calculate dimensions preserving aspect ratio
  let width: number
  let height: number

  if (aspectRatio > 1) {
    // Landscape
    width = Math.min(maxWidth, maxHeight * aspectRatio)
    height = width / aspectRatio
  } else if (aspectRatio < 1) {
    // Portrait
    height = maxHeight
    width = height * aspectRatio
  } else {
    // Square
    width = maxHeight
    height = maxHeight
  }

  return (
    <div className="mb-2">
      <div
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border-l-4 ${
          isSentByMe
            ? 'bg-sky-50/80 border-sky-400'
            : 'bg-gray-100 border-gray-400'
        }`}
      >
        {/* Label */}
        <span className={`text-xs font-medium ${isSentByMe ? 'text-sky-600' : 'text-gray-600'}`}>
          Gallery reply:
        </span>

        {/* Thumbnail with proper aspect ratio */}
        <div
          className="flex-shrink-0 rounded overflow-hidden bg-gray-200 shadow-sm"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <img
            src={galleryImage.thumbnail_url}
            alt="Gallery image"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  )
}
